/**
 * Life4Earth V2 — Role Definition Overrides API adapter
 *
 * DEV-1187 / DEV-1186 TU-2 Pattern B. Per-entity overlay of platform
 * role_definitions. Each org customizes platform baselines via sparse
 * override rows; non-overridden fields propagate from the source.
 *
 * Backed by `RoleDefinitionOverridesController` (5 endpoints):
 *   POST   /api/roledefinitions/{sourceGuid}/overrides
 *   PUT    /api/roledefinitions/overrides/{overrideGuid}
 *   DELETE /api/roledefinitions/overrides/{overrideGuid}
 *   GET    /api/roledefinitions/{sourceGuid}/overrides     (sysadmin-only)
 *   GET    /api/roledefinitions/effective?owningEntityGuid=X&pillarType=Y
 *
 * Phase 1 scoping: exact-entity-match. Override applies only when
 * caller's owningEntityGuid equals the override's owningEntityGuid.
 * Ancestor-walk scoping is a future enhancement.
 */

import { apiFetch } from "./client";

// ── Types ─────────────────────────────────────────────────────────

export interface RoleDefinitionOverrideResponse {
  overrideGuid: string;
  sourceDefinitionGuid: string;
  owningEntityGuid: string;
  label: string | null;
  description: string | null;
  roleCategory: string | null;
  dimensionWeights: string | null;
  skillsSuggested: string[] | null;
  concernCategories: string[] | null;
  timeCommitmentHint: string | null;
  permissionSetGuid: string | null;
  roleRhythmGuid: string | null;
  isActiveOverride: boolean | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Effective view of a role_definition for a specific entity — every
 * field COALESCEd between override (if any) and source. This is the
 * canonical "what roles can I assign on this entity" shape.
 */
export interface EffectiveRoleDefinitionResponse {
  definitionGuid: string;
  overrideGuid: string | null;
  hasOverride: boolean;
  roleCategory: string;
  label: string;
  description: string | null;
  dimensionWeights: string;
  skillsSuggested: string[];
  concernCategories: string[];
  timeCommitmentHint: string | null;
  isActive: boolean;
  sortOrder: number;
  pillarType: string;
  owningEntityGuid: string | null;
  roleRhythmGuid: string | null;
  permissionSetGuid: string | null;
}

export interface CreateOverrideRequest {
  owningEntityGuid: string;
  label?: string | null;
  description?: string | null;
  roleCategory?: string | null;
  dimensionWeights?: string | null;
  skillsSuggested?: string[] | null;
  concernCategories?: string[] | null;
  timeCommitmentHint?: string | null;
  permissionSetGuid?: string | null;     // sysadmin-only
  roleRhythmGuid?: string | null;        // sysadmin-only
  isActiveOverride?: boolean | null;
}

export interface UpdateOverrideRequest {
  label?: string | null;
  description?: string | null;
  roleCategory?: string | null;
  dimensionWeights?: string | null;
  skillsSuggested?: string[] | null;
  concernCategories?: string[] | null;
  timeCommitmentHint?: string | null;
  permissionSetGuid?: string | null;     // sysadmin-only
  roleRhythmGuid?: string | null;        // sysadmin-only
  isActiveOverride?: boolean | null;
}

// ── Fetchers ──────────────────────────────────────────────────────

export async function fetchEffectiveRoleDefinitions(
  owningEntityGuid: string,
  pillarType?: string,
): Promise<EffectiveRoleDefinitionResponse[]> {
  const params = new URLSearchParams({ owningEntityGuid });
  if (pillarType) params.set("pillarType", pillarType);
  const url = `/api/roledefinitions/effective?${params.toString()}`;
  return apiFetch<EffectiveRoleDefinitionResponse[]>(url);
}

export async function fetchOverridesForSource(
  sourceGuid: string,
): Promise<RoleDefinitionOverrideResponse[]> {
  return apiFetch<RoleDefinitionOverrideResponse[]>(
    `/api/roledefinitions/${sourceGuid}/overrides`,
  );
}

export async function createOverride(
  sourceGuid: string,
  body: CreateOverrideRequest,
): Promise<RoleDefinitionOverrideResponse> {
  return apiFetch<RoleDefinitionOverrideResponse>(
    `/api/roledefinitions/${sourceGuid}/overrides`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function updateOverride(
  overrideGuid: string,
  body: UpdateOverrideRequest,
): Promise<RoleDefinitionOverrideResponse> {
  return apiFetch<RoleDefinitionOverrideResponse>(
    `/api/roledefinitions/overrides/${overrideGuid}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export async function deleteOverride(overrideGuid: string): Promise<void> {
  await apiFetch<void>(
    `/api/roledefinitions/overrides/${overrideGuid}`,
    { method: "DELETE" },
  );
}
